import React from 'react';

/**
 * Garde-fou global contre les crashs de rendu React.
 * Au lieu d'un écran blanc total quand un composant lève une erreur
 * (import manquant, accès non protégé sur null, etc.), affiche un
 * message clair en français avec un bouton "Recharger".
 *
 * Usage : envelopper <App /> dans index.js.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Trace lisible en console pour diagnostic
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] React render crash:', error, info?.componentStack);
    // Remontée Sentry si dispo (initSentry déjà appelé dans index.js)
    try {
      if (typeof window !== 'undefined' && window.Sentry?.captureException) {
        window.Sentry.captureException(error, { extra: { componentStack: info?.componentStack } });
      }
    } catch (_) { /* ignore */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch (_) { /* ignore */ }
  };

  handleHome = () => {
    try { window.location.href = '/'; } catch (_) { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-6 py-12"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-red-50 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-8 h-8 text-red-500"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Une erreur s'est produite
          </h1>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">
            L'application a rencontré un problème inattendu. Cliquez sur
            « Recharger » pour relancer la page. Si l'erreur persiste, contactez
            l'administrateur.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-[#2E7D32] text-white text-sm font-medium hover:bg-[#256628] transition-colors"
              data-testid="error-boundary-reload-btn"
            >
              Recharger la page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors"
              data-testid="error-boundary-home-btn"
            >
              Retour à l'accueil
            </button>
          </div>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre className="mt-6 text-xs text-left text-red-600 bg-red-50 p-3 rounded overflow-auto max-h-40">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
